'use client';

import { useState, useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { Plus, Sparkles, Image } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { cn } from '@/shared/lib/cn';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('FloatingActionButton');

interface ActionItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  color?: string;
}

export function FloatingActionButton() {
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  // Determine context-aware actions based on current route
  const projectMatch = pathname.match(/\/project\/([^/]+)/);
  const conceptMatch = pathname.match(/\/concept\/([^/]+)/);
  const projectId = projectMatch?.[1];
  const conceptId = conceptMatch?.[1];

  const actions: ActionItem[] = [];

  // Context-aware actions
  if (projectId && !conceptId) {
    actions.push({
      label: 'New Concept',
      icon: Image,
      onClick: () => {
        setIsOpen(false);
        router.push(`/project/${projectId}?action=new-concept`);
      },
      color: 'bg-primary hover:bg-primary/90',
    });
  } else if (conceptId) {
    actions.push({
      label: 'Create Render',
      icon: Sparkles,
      onClick: () => {
        setIsOpen(false);
        // Removed navigation to render page - should open render settings drawer
        logger.info('Render action clicked - should open settings drawer');
      },
      color: 'bg-primary hover:bg-primary/90',
    });
  }

  // Close menu when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  // Don't show FAB if no actions available or on certain pages
  if (
    actions.length === 0 ||
    pathname.includes('/settings') ||
    pathname.includes('/auth')
  ) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className={cn(
            'fixed inset-0 bg-background/20 backdrop-blur-sm z-40 md:hidden',
            'animate-in fade-in-0 duration-200'
          )}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* FAB Container */}
      <div className="fixed bottom-20 right-4 z-50 md:hidden">
        {/* Action Menu */}
        {isOpen && (
          <div
            className={cn(
              'absolute bottom-16 right-0 mb-2',
              'animate-in fade-in-0 zoom-in-95 duration-200'
            )}
          >
            <div className="flex flex-col items-end space-y-2">
              {actions.map((action, index) => (
                <Button
                  key={index}
                  onClick={action.onClick}
                  variant="secondary"
                  className="rounded-full shadow-lg"
                >
                  <span>{action.label}</span>
                  <action.icon className="w-5 h-5 ml-2" />
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Main FAB Button */}
        <Button
          onClick={() => setIsOpen(!isOpen)}
          size="icon"
          className={cn(
            'rounded-full w-14 h-14 shadow-lg',
            'bg-primary hover:bg-primary/90',
            isOpen && 'rotate-45',
            'transition-all duration-200'
          )}
        >
          <Plus className="w-6 h-6" />
        </Button>
      </div>
    </>
  );
}
